import type { AttentionItemPublic, DashboardSummaryResponse, RecentTransactionPublic } from '@/api/type/dashboard';
import type { DashboardAttentionItem, DashboardSummary, DashboardTransaction } from '@/types';
import { toThreeDigits } from '@/lib/utils';
import { formatChartMonth } from '@/lib/months';

export const AttentionItemMapper = (item: AttentionItemPublic): DashboardAttentionItem => ({
  memberId: String(item.user_id),
  memberNo: item.member_id != null ? toThreeDigits(item.member_id) : undefined,
  memberName: item.user_name,
  cardNo: item.card_no || undefined,
  policyNo: item.policy_no,
  loanNo: item.loan_no || undefined,
  kind: item.kind,
  amountDue: item.amount_due,
  note: item.note,
});

export const TransactionMapper = (item: RecentTransactionPublic): DashboardTransaction => ({
  date: item.date,
  memberId: String(item.user_id),
  memberNo: item.member_id != null ? toThreeDigits(item.member_id) : undefined,
  memberName: item.user_name,
  cardNo: item.card_no || undefined,
  policyNo: item.policy_no || undefined,
  loanNo: item.loan_no || undefined,
  kind: item.kind,
  paymentType: item.payment_type,
  amount: item.amount,
});

export const DashboardSummaryMapper = (response: DashboardSummaryResponse): DashboardSummary => {
  const { kpis } = response;

  return {
    month: response.month,
    kpis: {
      totalMembers: kpis.total_members,
      totalPolicies: kpis.total_policies,
      paidThisMonth: kpis.paid_this_month,
      policyPaid: kpis.policy_paid,
      policyLive: kpis.policy_live,
      loanPaid: kpis.loan_paid,
      loanDue: kpis.loan_due,
      unpaidThisMonth: kpis.unpaid_this_month,
      collectedThisMonth: kpis.collected_this_month,
      policyCollectedThisMonth: kpis.policy_collected_this_month,
      loanCollectedThisMonth: kpis.loan_collected_this_month,
      lifetimeCollected: kpis.lifetime_collected,
      loanOutstanding: kpis.loan_outstanding,
      loansActive: kpis.loans_active,
      loansGiven: kpis.loans_given,
      policyTargetThisMonth: kpis.policy_target_this_month,
      memberBalanceDue: kpis.member_balance_due ?? 0,
      membersWithBalance: kpis.members_with_balance ?? 0,
      balanceInterestDue: kpis.balance_interest_due ?? 0,
      paidByPmsThisMonth: kpis.paid_by_pms_this_month ?? 0,
    },
    memberStats: response.member_stats
      ? {
          totalMembers: response.member_stats.total_members,
          male: response.member_stats.male_members,
          female: response.member_stats.female_members,
          others: response.member_stats.other_members,
          joinedThisMonth: response.member_stats.joined_this_month,
          withPolicy: response.member_stats.members_with_policy,
          withoutPolicy: response.member_stats.members_without_policy,
          under5: response.member_stats.members_under_5,
          age5To17: response.member_stats.members_5_to_17,
          age18To59: response.member_stats.members_18_to_59,
          age60AndAbove: response.member_stats.members_60_and_above,
          alive: response.member_stats.alive_members,
          deceased: response.member_stats.deceased_members,
          claimsSettled: response.member_stats.claims_settled,
        }
      : null,
    monthlyCollection: response.monthly_collection.map((point) => ({
      label: formatChartMonth(point.month),
      policyExpected: point.policy_expected,
      policy: point.policy,
      loan: point.loan,
    })),
    memberGrowth: response.member_growth.map((point) => ({
      label: formatChartMonth(point.month),
      total: point.total_members,
      added: point.new_members,
    })),
    policyGrowth: response.policy_growth.map((point) => ({
      label: formatChartMonth(point.month),
      total: point.total_policies,
      added: point.new_policies,
    })),
    membershipStatus: response.membership_status,
    paymentMethods: { cash: response.payment_methods.cash, online: response.payment_methods.online, pms: response.payment_methods.pms ?? 0 },
    memberBalance: {
      repaid: response.member_balance?.repaid ?? 0,
      balanceDue: response.member_balance?.balance_due ?? 0,
      interestDue: response.member_balance?.interest_due ?? 0,
    },
    loanPortfolio: {
      principalRepaid: response.loan_portfolio.principal_repaid,
      outstanding: response.loan_portfolio.outstanding,
      given: response.loan_portfolio.given,
    },
    loanStatus: {
      withActiveLoan: response.loan_status.with_active_loan,
      eligible: response.loan_status.eligible,
      notEligible: response.loan_status.not_eligible,
    },
    attention: response.attention.map(AttentionItemMapper),
    recentTransactions: response.recent_transactions.map(TransactionMapper),
  };
};
